import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/common/components/ui/button';
import { PaginationControls } from '@/common/components/ui/pagination-controls';
import { Skeleton } from '@/common/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/common/components/ui/table';
import { formatLastUpdated } from '@/common/utils/DateFormatters';
import { PolicyFilters, PolicySummary } from '@/policy/api/FetchPolicyList';
import { useScroll } from '@/policy/hooks/useScroll';
import { PolicySortField, SortOrder } from '@/policy/types/SortTypes';

import { ScrollShadows } from '../ScrollShadows/ScrollShadows';
import { PolicyListHeader } from './PolicyListHeader';
import { PolicyListRow } from './PolicyListRow';
import { PolicyListSkeleton } from './PolicyListSkeleton';

interface PolicyListProps {
  policies: PolicySummary[];
  isLoading: boolean;
  totalPages: number;
  totalPolicies: number;
  currentPage: number;
  pageSize: number;
  sortBy: PolicySortField;
  sortOrder: SortOrder;
  filters: PolicyFilters;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (field: PolicySortField) => void;
  onFilterChange: (field: string, value: string | string[]) => void;
  onRefresh: () => void;
  lastRefreshedAt: Date;
}

export const PolicyList = ({
  policies,
  isLoading,
  totalPages,
  totalPolicies,
  currentPage,
  pageSize,
  sortBy,
  sortOrder,
  filters,
  onPageChange,
  onPageSizeChange,
  onSort,
  onFilterChange,
  onRefresh,
  lastRefreshedAt,
}: PolicyListProps) => {
  const { t, i18n } = useTranslation();
  const [isSpinning, setIsSpinning] = useState(false);
  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    canScrollDown,
    canScrollUp,
  } = useScroll();

  const handleRefresh = () => {
    setIsSpinning(true);
    onRefresh();
    setTimeout(() => {
      setIsSpinning(false);
    }, 2000);
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex flex-row items-center justify-end gap-x-2 pb-4 shrink-0">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-48 bg-muted-foreground/20" />
            <Skeleton className="h-8 w-8 bg-muted-foreground/20 rounded-md" />
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground text-xs font-light leading-4">
              {t('common.lastUpdatedAt', {
                updatedAt: formatLastUpdated(lastRefreshedAt, i18n.language),
              })}
            </p>
            <Button
              aria-label={t('common.refresh')}
              onClick={handleRefresh}
              disabled={isSpinning}
              variant="secondary"
              size="sm"
            >
              <RefreshCw
                className={`h-4 w-4 ${isSpinning && 'animate-spin'}`}
              />
            </Button>
          </>
        )}
      </div>

      <ScrollShadows
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        canScrollDown={canScrollDown}
        canScrollUp={canScrollUp}
        className="rounded-md border border-border bg-background shadow-sm overflow-hidden flex-1 flex flex-col min-h-0 min-w-0"
        offsets={{
          top: 40,
          left: 0,
          bottom: 0,
          right: 0,
        }}
      >
        <div
          ref={scrollContainerRef}
          className={`overflow-x-auto overflow-y-auto flex-1 z-0
          [&::-webkit-scrollbar]:h-1.5
          [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
          [&::-webkit-scrollbar-thumb]:rounded-full`}
        >
          <Table noWrapper className="table-fixed w-full">
            <PolicyListHeader
              sortBy={sortBy}
              sortOrder={sortOrder}
              filters={filters}
              onSort={onSort}
              onFilterChange={onFilterChange}
            />
            {isLoading ? (
              <PolicyListSkeleton rowCount={pageSize} />
            ) : (
              <TableBody>
                {policies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center">
                      {t('policy.noPoliciesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  policies.map((policy) => (
                    <PolicyListRow key={policy.uuid} policy={policy} />
                  ))
                )}
              </TableBody>
            )}
          </Table>
        </div>
      </ScrollShadows>

      <div className="pt-4 shrink-0">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalPolicies}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
    </div>
  );
};
